import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiSave, FiFileText, FiAlertCircle, FiHash } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { doc, getDoc, setDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import { getDocCounterCurrent, setDocCounterNext } from '../../utils/transactionHelper';

// Document-setting key → TranType code, for the live "next number" counters.
// (Keys without a transaction code, e.g. stockAdjustment, are omitted.)
const DOC_KEY_TO_CODE = {
  purchaseOrder: 'PO', grn: 'GRN', purchaseInvoice: 'PI', purchaseReturn: 'PR',
  nonTaxPurchaseInvoice: 'NPI', nonTaxPurchaseReturn: 'NPR', importPurchaseInvoice: 'IPI',
  quotation: 'QT', saleOrder: 'SO', deliveryChallan: 'DC', saleInvoice: 'SI', saleReturn: 'SR',
  nonTaxSaleInvoice: 'NSI', nonTaxSaleReturn: 'NSR', posCounterSale: 'PS',
  cashReceipt: 'CR', bankReceipt: 'BR', cashPayment: 'CP', bankPayment: 'BP',
  journalVoucher: 'JV', cashBook: 'CB',
  inwardGatePass: 'IGP', outwardGatePass: 'OGP', stockTransfer: 'ST', branchTransfer: 'BT',
  billOfMaterials: 'BOM', productionOrder: 'PRO', addStock: 'AS', reduceStock: 'RS',
  salaryAdvance: 'ADV', staffLoan: 'LON', payrollRun: 'PAY', finalSettlement: 'FNF',
  loanRecovery: 'LRC'
};

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const FormCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${colors.borderRadius.md};
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const CardBody = styled.div`
  padding: 24px;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'success' ? colors.success.main + '15' : colors.error.main + '15'};
  color: ${props => props.$type === 'success' ? colors.success.main : colors.error.main};
  border: 1px solid ${props => props.$type === 'success' ? colors.success.main + '30' : colors.error.main + '30'};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[200]};

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  vertical-align: middle;
`;

const DocumentType = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DocumentName = styled.span`
  font-weight: 500;
  color: ${colors.text.primary};
`;

const DocumentDescription = styled.span`
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  width: ${props => props.$width || '100px'};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &:disabled {
    background: ${colors.neutral[100]};
    cursor: not-allowed;
  }
`;

const NumberInput = styled(Input)`
  width: 100px;
  text-align: center;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
`;

const PreviewBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  background: ${colors.primary.main}10;
  border: 1px solid ${colors.primary.main}30;
  border-radius: ${colors.borderRadius.md};
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 13px;
  color: ${colors.primary.main};
  font-weight: 500;
`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InfoBox = styled.div`
  background: ${colors.primary.main}10;
  border: 1px solid ${colors.primary.main}30;
  border-radius: ${colors.borderRadius.md};
  padding: 16px;
  margin-bottom: 24px;
  font-size: 13px;
  color: ${colors.text.secondary};
  line-height: 1.6;

  strong {
    color: ${colors.text.primary};
  }

  code {
    background: ${colors.neutral[100]};
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SF Mono', 'Consolas', monospace;
    font-size: 12px;
    color: ${colors.primary.main};
  }
`;

const defaultDocumentSettings = {
  // Purchase Documents
  purchaseRequisition: { prefix: 'PR', startNumber: 1, currentNumber: 0, padding: 5 },
  purchaseOrder: { prefix: 'PO', startNumber: 1, currentNumber: 0, padding: 5 },
  grn: { prefix: 'GRN', startNumber: 1, currentNumber: 0, padding: 5 },
  purchaseInvoice: { prefix: 'PI', startNumber: 1, currentNumber: 0, padding: 5 },
  purchaseReturn: { prefix: 'PDN', startNumber: 1, currentNumber: 0, padding: 5 },
  nonTaxPurchaseInvoice: { prefix: 'NPI', startNumber: 1, currentNumber: 0, padding: 5 },
  nonTaxPurchaseReturn: { prefix: 'NPR', startNumber: 1, currentNumber: 0, padding: 5 },
  importPurchaseInvoice: { prefix: 'IPI', startNumber: 1, currentNumber: 0, padding: 5 },

  // Sales Documents
  quotation: { prefix: 'QT', startNumber: 1, currentNumber: 0, padding: 5 },
  saleOrder: { prefix: 'SO', startNumber: 1, currentNumber: 0, padding: 5 },
  deliveryChallan: { prefix: 'DC', startNumber: 1, currentNumber: 0, padding: 5 },
  saleInvoice: { prefix: 'SI', startNumber: 1, currentNumber: 0, padding: 5 },
  saleReturn: { prefix: 'SCN', startNumber: 1, currentNumber: 0, padding: 5 },
  nonTaxSaleInvoice: { prefix: 'NSI', startNumber: 1, currentNumber: 0, padding: 5 },
  nonTaxSaleReturn: { prefix: 'NSR', startNumber: 1, currentNumber: 0, padding: 5 },
  posCounterSale: { prefix: 'POS', startNumber: 1, currentNumber: 0, padding: 5 },

  // Receipt & Payment Documents
  cashReceipt: { prefix: 'CR', startNumber: 1, currentNumber: 0, padding: 5 },
  bankReceipt: { prefix: 'BR', startNumber: 1, currentNumber: 0, padding: 5 },
  cashPayment: { prefix: 'CP', startNumber: 1, currentNumber: 0, padding: 5 },
  bankPayment: { prefix: 'BP', startNumber: 1, currentNumber: 0, padding: 5 },

  // Journal Documents
  journalVoucher: { prefix: 'JV', startNumber: 1, currentNumber: 0, padding: 5 },
  cashBook: { prefix: 'CB', startNumber: 1, currentNumber: 0, padding: 5 },

  // Store Documents
  inwardGatePass: { prefix: 'IGP', startNumber: 1, currentNumber: 0, padding: 5 },
  outwardGatePass: { prefix: 'OGP', startNumber: 1, currentNumber: 0, padding: 5 },
  stockAdjustment: { prefix: 'ADJ', startNumber: 1, currentNumber: 0, padding: 5 },
  addStock: { prefix: 'ADS', startNumber: 1, currentNumber: 0, padding: 5 },
  reduceStock: { prefix: 'RDS', startNumber: 1, currentNumber: 0, padding: 5 },
  stockTransfer: { prefix: 'TRF', startNumber: 1, currentNumber: 0, padding: 5 },
  branchTransfer: { prefix: 'BT', startNumber: 1, currentNumber: 0, padding: 5 },
  billOfMaterials: { prefix: 'BOM', startNumber: 1, currentNumber: 0, padding: 5 },
  productionOrder: { prefix: 'PROD', startNumber: 1, currentNumber: 0, padding: 5 },

  // Salary Documents
  salaryAdvance: { prefix: 'ADV', startNumber: 1, currentNumber: 0, padding: 5 },
  staffLoan: { prefix: 'LON', startNumber: 1, currentNumber: 0, padding: 5 },
  payrollRun: { prefix: 'PAY', startNumber: 1, currentNumber: 0, padding: 5 },
  finalSettlement: { prefix: 'FNF', startNumber: 1, currentNumber: 0, padding: 5 },
  loanRecovery: { prefix: 'LRC', startNumber: 1, currentNumber: 0, padding: 5 }
};

const documentTypes = [
  { key: 'purchaseRequisition', name: 'Purchase Requisition', description: 'Internal purchase requests', category: 'Purchase' },
  { key: 'purchaseOrder', name: 'Purchase Order', description: 'Orders to vendors', category: 'Purchase' },
  { key: 'grn', name: 'Goods Receipt Note', description: 'Goods received from vendors', category: 'Purchase' },
  { key: 'purchaseInvoice', name: 'Purchase Invoice', description: 'Vendor bills', category: 'Purchase' },
  { key: 'purchaseReturn', name: 'Purchase Return', description: 'Debit notes to vendors', category: 'Purchase' },
  { key: 'nonTaxPurchaseInvoice', name: 'Non-Tax Purchase Invoice', description: 'Purchase bills without GST', category: 'Purchase' },
  { key: 'nonTaxPurchaseReturn', name: 'Non-Tax Purchase Return', description: 'Returns without GST', category: 'Purchase' },
  { key: 'importPurchaseInvoice', name: 'Import Purchase Invoice', description: 'Foreign vendor bills with landed cost', category: 'Purchase' },

  { key: 'quotation', name: 'Quotation', description: 'Price quotes to customers', category: 'Sales' },
  { key: 'saleOrder', name: 'Sale Order', description: 'Customer orders', category: 'Sales' },
  { key: 'deliveryChallan', name: 'Delivery Challan', description: 'Delivery documents', category: 'Sales' },
  { key: 'saleInvoice', name: 'Sale Invoice', description: 'Customer bills', category: 'Sales' },
  { key: 'saleReturn', name: 'Sale Return', description: 'Credit notes to customers', category: 'Sales' },
  { key: 'nonTaxSaleInvoice', name: 'Non-Tax Sale Invoice', description: 'Sale invoices without GST', category: 'Sales' },
  { key: 'nonTaxSaleReturn', name: 'Non-Tax Sale Return', description: 'Returns without GST', category: 'Sales' },
  { key: 'posCounterSale', name: 'POS Counter Sale', description: 'Point-of-sale receipts', category: 'Sales' },

  { key: 'cashReceipt', name: 'Cash Receipt', description: 'Cash received', category: 'Receipt & Payment' },
  { key: 'bankReceipt', name: 'Bank Receipt', description: 'Bank deposits', category: 'Receipt & Payment' },
  { key: 'cashPayment', name: 'Cash Payment', description: 'Cash paid', category: 'Receipt & Payment' },
  { key: 'bankPayment', name: 'Bank Payment', description: 'Bank payments', category: 'Receipt & Payment' },

  { key: 'journalVoucher', name: 'Journal Voucher', description: 'Manual accounting entries', category: 'Journal' },
  { key: 'cashBook', name: 'Cash Book', description: 'Daily cash register voucher', category: 'Journal' },

  { key: 'inwardGatePass', name: 'Inward Gate Pass', description: 'Goods entering premises', category: 'Store' },
  { key: 'outwardGatePass', name: 'Outward Gate Pass', description: 'Goods leaving premises', category: 'Store' },
  { key: 'addStock', name: 'Add Stock', description: 'Stock additions (found / gift / opening)', category: 'Store' },
  { key: 'reduceStock', name: 'Reduce Stock', description: 'Stock reductions (lost / damaged / write-off)', category: 'Store' },
  { key: 'stockTransfer', name: 'Stock Transfer', description: 'Inter-location transfers', category: 'Store' },
  { key: 'branchTransfer', name: 'Branch Transfer', description: 'Inter-branch transfers', category: 'Store' },
  { key: 'billOfMaterials', name: 'Bill of Materials', description: 'Product recipes', category: 'Store' },
  { key: 'productionOrder', name: 'Production Order', description: 'Manufacturing orders', category: 'Store' },

  { key: 'salaryAdvance', name: 'Salary Advance', description: 'Staff advance vouchers', category: 'Salary' },
  { key: 'staffLoan', name: 'Staff Loan', description: 'Staff loan agreements', category: 'Salary' },
  { key: 'payrollRun', name: 'Payroll Run', description: 'Monthly payroll', category: 'Salary' },
  { key: 'finalSettlement', name: 'Final Settlement', description: 'Staff F&F', category: 'Salary' },
  { key: 'loanRecovery', name: 'Loan Recovery', description: 'Loan / advance paid back in cash or bank', category: 'Salary' }
];

const DocumentSettings = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch, isModuleEnabled } = useAuth();
  const { canEdit } = usePagePermissions('settings', 'documentSettings');
  const [settings, setSettings] = useState(defaultDocumentSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // ── Live "next number" counters (per type, per branch, per financial year) ──
  const branchEnabled = isModuleEnabled && isModuleEnabled('settings_branches');
  const [financialYears, setFinancialYears] = useState([]);
  const [branches, setBranches] = useState([]);
  const [liveFY, setLiveFY] = useState('');
  const [liveBranch, setLiveBranch] = useState(''); // '' = no branch / company-level
  const [counters, setCounters] = useState({});     // docKey -> current (last issued)
  const [nextInputs, setNextInputs] = useState({}); // docKey -> string
  const [countersLoading, setCountersLoading] = useState(false);
  const [savingCounterKey, setSavingCounterKey] = useState('');

  // Load financial years + branches once
  useEffect(() => {
    if (!activeCompany?.id) return;
    (async () => {
      try {
        const fySnap = await getDocs(query(collection(db, 'companies', activeCompany.id, 'financialYears'), orderBy('startDate', 'desc')));
        const fys = fySnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setFinancialYears(fys);
        setLiveFY(activeFinancialYear?.id || (fys[0]?.id || ''));
      } catch (e) { console.error('FY load failed:', e); }
      if (branchEnabled) {
        try {
          const bSnap = await getDocs(collection(db, 'companies', activeCompany.id, 'branches'));
          setBranches(bSnap.docs.map(d => ({ id: d.id, ...d.data() })));
          if (activeBranch && activeBranch.id && activeBranch.id !== 'all') setLiveBranch(activeBranch.id);
        } catch (e) { console.error('Branch load failed:', e); }
      }
    })();
  }, [activeCompany, activeFinancialYear, activeBranch, branchEnabled]);

  // Load the live counters for the selected FY + branch
  const loadCounters = useCallback(async () => {
    if (!activeCompany?.id || !liveFY) { setCounters({}); return; }
    setCountersLoading(true);
    try {
      const keys = Object.keys(DOC_KEY_TO_CODE);
      const results = await Promise.all(keys.map(k =>
        getDocCounterCurrent(activeCompany.id, DOC_KEY_TO_CODE[k], liveBranch || null, liveFY)
      ));
      const map = {};
      keys.forEach((k, i) => { map[k] = results[i]; });
      setCounters(map);
      setNextInputs({});
    } catch (e) {
      console.error('Counter load failed:', e);
    } finally {
      setCountersLoading(false);
    }
  }, [activeCompany, liveFY, liveBranch]);

  useEffect(() => { loadCounters(); }, [loadCounters]);

  const handleSetNext = async (docKey) => {
    if (!canEdit) { alert('You do not have permission to change numbering.'); return; }
    const code = DOC_KEY_TO_CODE[docKey];
    const val = Number(nextInputs[docKey]);
    if (!code || !val || val < 1) { setAlert({ show: true, type: 'error', message: 'Enter a valid next number.' }); return; }
    setSavingCounterKey(docKey);
    try {
      await setDocCounterNext(activeCompany.id, code, liveBranch || null, liveFY, val);
      setCounters(prev => ({ ...prev, [docKey]: val - 1 }));
      setNextInputs(prev => ({ ...prev, [docKey]: '' }));
      setAlert({ show: true, type: 'success', message: `Next ${docKey} number set to ${val}.` });
    } catch (e) {
      setAlert({ show: true, type: 'error', message: e?.message || 'Failed to set next number.' });
    } finally {
      setSavingCounterKey('');
    }
  };

  const loadSettings = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);
      const settingsRef = doc(db, 'companies', activeCompany.id, 'settings', 'documentNumbering');
      const snapshot = await getDoc(settingsRef);

      if (snapshot.exists()) {
        const data = snapshot.data();
        // Merge with defaults to ensure all keys exist
        setSettings({
          ...defaultDocumentSettings,
          ...data
        });
      } else {
        // Use defaults if no settings exist
        setSettings(defaultDocumentSettings);
      }
    } catch (error) {
      console.error('Error loading document settings:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to load document settings' });
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handlePrefixChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        prefix: value.toUpperCase()
      }
    }));
  };

  const handleStartNumberChange = (key, value) => {
    const num = parseInt(value) || 1;
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        startNumber: num,
        currentNumber: prev[key].currentNumber < num ? num - 1 : prev[key].currentNumber
      }
    }));
  };

  const handlePaddingChange = (key, value) => {
    const num = parseInt(value) || 4;
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        padding: Math.min(Math.max(num, 3), 8) // Between 3 and 8
      }
    }));
  };

  const generatePreview = (setting) => {
    const year = new Date().getFullYear();
    const nextNumber = (setting.currentNumber || 0) + 1;
    const paddedNumber = String(nextNumber).padStart(setting.padding, '0');
    return `${setting.prefix}-${year}-${paddedNumber}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }

    setSaving(true);
    try {
      const settingsRef = doc(db, 'companies', activeCompany.id, 'settings', 'documentNumbering');

      await setDoc(settingsRef, {
        ...settings,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid
      });

      setAlert({ show: true, type: 'success', message: 'Document settings saved successfully' });
    } catch (error) {
      console.error('Error saving document settings:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to save document settings' });
    } finally {
      setSaving(false);
    }
  };

  const groupedDocuments = documentTypes.reduce((acc, docItem) => {
    if (!acc[docItem.category]) {
      acc[docItem.category] = [];
    }
    acc[docItem.category].push(docItem);
    return acc;
  }, {});

  return (
    <PageLayout pageTitle="Document Settings">
      <Container>
        <PageHeader>
          <PageTitle>Document Numbering</PageTitle>
          <PageDescription>
            Configure automatic document numbering prefixes and formats for all transaction types.
          </PageDescription>
        </PageHeader>

        {alert.show && (
          <Alert $type={alert.type}>
            <FiAlertCircle />
            {alert.message}
          </Alert>
        )}

        <InfoBox>
          <strong>Document Number Format:</strong> <code>PREFIX-[BRANCH-]FY-NUMBER</code><br /><br />
          • <strong>Prefix:</strong> per document type (configured below)<br />
          • <strong>Branch:</strong> branch code is inserted when multi-branch is on (e.g. <code>LHR</code>)<br />
          • <strong>FY:</strong> financial-year start year (e.g. <code>2025</code> for FY 2025-26) — numbering resets each financial year<br />
          • <strong>Number:</strong> auto-incremented, zero-padded; never reused after a delete<br /><br />
          Example: <code>SI-LHR-2025-00001</code> (with branch) or <code>SI-2025-00001</code>
        </InfoBox>

        {/* Live "Set Next Number" control — writes the real per-(type, branch, FY) counter */}
        <FormCard>
          <CardHeader>
            <CardIcon><FiHash /></CardIcon>
            <CardTitle>Set Next Number (live counters)</CardTitle>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Label>Financial Year</Label>
                <Input as="select" value={liveFY} onChange={e => setLiveFY(e.target.value)} style={{ width: 220 }}>
                  {financialYears.map(fy => (
                    <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode || fy.id}{fy.isActive ? ' (Active)' : ''}</option>
                  ))}
                </Input>
              </div>
              {branchEnabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Label>Branch</Label>
                  <Input as="select" value={liveBranch} onChange={e => setLiveBranch(e.target.value)} style={{ width: 220 }}>
                    <option value="">— No branch / Company —</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.branchCode ? b.branchCode + ' - ' : ''}{b.branchName}</option>
                    ))}
                  </Input>
                </div>
              )}
            </div>
            <InfoBox style={{ marginBottom: 16 }}>
              <strong>Current</strong> = the last issued number for the selected year{branchEnabled ? '/branch' : ''}. Set a new <strong>Next #</strong> to change where the next document starts. You can't set it below a number already used.
            </InfoBox>
            <Table>
              <Thead>
                <tr>
                  <Th style={{ width: '45%' }}>Document Type</Th>
                  <Th style={{ width: '20%' }}>Current (last used)</Th>
                  <Th style={{ width: '20%' }}>Next #</Th>
                  <Th style={{ width: '15%' }}></Th>
                </tr>
              </Thead>
              <Tbody>
                {documentTypes.filter(dt => DOC_KEY_TO_CODE[dt.key]).map(dt => (
                  <Tr key={dt.key}>
                    <Td>{dt.name} <span style={{ color: colors.text.tertiary, fontSize: 12 }}>({DOC_KEY_TO_CODE[dt.key]})</span></Td>
                    <Td>{countersLoading ? '…' : (counters[dt.key] || 0)}</Td>
                    <Td>
                      <NumberInput
                        type="number" min={1}
                        value={nextInputs[dt.key] || ''}
                        placeholder={String((counters[dt.key] || 0) + 1)}
                        onChange={e => setNextInputs(prev => ({ ...prev, [dt.key]: e.target.value }))}
                      />
                    </Td>
                    <Td>
                      {canEdit && (
                        <SaveButton type="button" style={{ padding: '6px 14px' }} disabled={savingCounterKey === dt.key} onClick={() => handleSetNext(dt.key)}>
                          {savingCounterKey === dt.key ? '…' : 'Set'}
                        </SaveButton>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </FormCard>

        {loading ? (
          <FormCard>
            <CardBody style={{ textAlign: 'center', padding: '60px 20px' }}>
              Loading settings...
            </CardBody>
          </FormCard>
        ) : (
          <form onSubmit={handleSubmit}>
            {Object.entries(groupedDocuments).map(([category, docs]) => (
              <FormCard key={category}>
                <CardHeader>
                  <CardIcon>
                    <FiFileText />
                  </CardIcon>
                  <CardTitle>{category} Documents</CardTitle>
                </CardHeader>
                <CardBody style={{ padding: 0 }}>
                  <Table>
                    <Thead>
                      <tr>
                        <Th style={{ width: '30%' }}>Document Type</Th>
                        <Th style={{ width: '15%' }}>Prefix</Th>
                        <Th style={{ width: '15%' }}>Start #</Th>
                        <Th style={{ width: '15%' }}>Padding</Th>
                        <Th style={{ width: '25%' }}>Preview</Th>
                      </tr>
                    </Thead>
                    <Tbody>
                      {docs.map(docType => (
                        <Tr key={docType.key}>
                          <Td>
                            <DocumentType>
                              <DocumentName>{docType.name}</DocumentName>
                              <DocumentDescription>{docType.description}</DocumentDescription>
                            </DocumentType>
                          </Td>
                          <Td>
                            <Input
                              type="text"
                              value={settings[docType.key]?.prefix || ''}
                              onChange={(e) => handlePrefixChange(docType.key, e.target.value)}
                              maxLength={6}
                              $width="80px"
                            />
                          </Td>
                          <Td>
                            <NumberInput
                              type="number"
                              value={settings[docType.key]?.startNumber || 1}
                              onChange={(e) => handleStartNumberChange(docType.key, e.target.value)}
                              min={1}
                            />
                          </Td>
                          <Td>
                            <NumberInput
                              type="number"
                              value={settings[docType.key]?.padding || 5}
                              onChange={(e) => handlePaddingChange(docType.key, e.target.value)}
                              min={3}
                              max={8}
                            />
                          </Td>
                          <Td>
                            <PreviewBadge>
                              {generatePreview(settings[docType.key] || defaultDocumentSettings[docType.key])}
                            </PreviewBadge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </CardBody>
              </FormCard>
            ))}

            {canEdit && (
              <FormCard>
                <SaveButtonContainer style={{ border: 'none' }}>
                  <SaveButton type="submit" disabled={saving}>
                    <FiSave />
                    {saving ? 'Saving...' : 'Save Settings'}
                  </SaveButton>
                </SaveButtonContainer>
              </FormCard>
            )}
          </form>
        )}
      </Container>
    </PageLayout>
  );
};

export default DocumentSettings;
